import z from 'zod'
import { type ReactNode, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import {
  isDefined,
  isNotEmpty,
  searchIncludes,
  getCreatedAt,
  MilestoneSchema,
  PersistedDateRangeSchema,
  type PersistedDateRange,
} from '@terros/common'
import { type SortingState } from '@tanstack/react-table'
import { createFileRoute } from '@tanstack/react-router'
import { Stack, TextInput } from '@mantine/core'
import { useStoredArray, useStoredQuery } from '@hooks'
import { fromPersistedDateRange, useFeatures } from '@front/model'
import { NotReady, Page } from '@components'
import { type MilestoneFilterValue } from './MilestoneMultiSelect'
import { FeatureTable } from './FeatureTable'
import { FeatureFilters, type ActiveFilter } from './FeatureFilters'

const ALL_TIME: PersistedDateRange = { type: 'allTime' }
const ActiveFilterSchema = z.enum(['all', 'active', 'inactive'])
const MilestoneFilterValueSchema = z.union([MilestoneSchema, z.literal('none')])

function Features(): ReactNode {
  const { data, isLoading, error, refetch, isFetching } = useFeatures()
  const [query, setQuery] = useState('')
  const [sorting, setSorting] = useState<SortingState>([{ id: 'activeState', desc: true }])

  const { value: activeFilter, setValue: setActiveFilter } = useStoredQuery<ActiveFilter>({
    defaultValue: 'all',
    storedKey: 'feature-activeFilter',
    urlKey: 'activeFilter',
    schema: ActiveFilterSchema,
  })
  const { value: persistedRange, setValue: setPersistedRange } = useStoredQuery<PersistedDateRange>({
    defaultValue: ALL_TIME,
    storedKey: 'feature-dateRange',
    urlKey: 'dateRange',
    schema: PersistedDateRangeSchema,
  })
  const { selectedItems: milestones, setSelectedItems: setMilestones } = useStoredArray<MilestoneFilterValue>({
    defaultValue: [],
    storedKey: 'feature-milestones',
    urlKey: 'milestones',
    schema: MilestoneFilterValueSchema,
  })

  const { value: missingRolloutIssue, setValue: setMissingRolloutIssue } = useStoredQuery<boolean>({
    defaultValue: false,
    storedKey: 'feature-missingRolloutIssue',
    urlKey: 'missingRolloutIssue',
    schema: z.boolean(),
  })

  const dateRange = useMemo(() => {
    const { startTime, endTime } = fromPersistedDateRange(persistedRange)
    return [startTime ? new Date(startTime) : null, endTime ? new Date(endTime) : null] as [Date | null, Date | null]
  }, [persistedRange])

  const today = useMemo(() => DateTime.now().toISODate(), [])

  const filtered = useMemo(() => {
    return (data ?? []).filter((feature) => {
      if (isNotEmpty(query)) {
        const searchables = [feature.featureId, feature.milestone, feature.rolloutIssueId]
        if (!searchIncludes(searchables, query)) return false
      }

      if (missingRolloutIssue && isDefined(feature.rolloutIssueId)) return false

      if (activeFilter === 'active' && feature.lastUsed !== today) return false
      if (activeFilter === 'inactive' && feature.lastUsed === today) return false

      if (dateRange[0] || dateRange[1]) {
        const createdAt = getCreatedAt(feature)
        if (!createdAt) return false
        if (dateRange[0] && createdAt < dateRange[0].getTime()) return false
        if (dateRange[1] && createdAt > dateRange[1].getTime()) return false
      }

      if (isNotEmpty(milestones)) {
        const featureMilestone: MilestoneFilterValue = feature.milestone ?? 'none'
        if (!milestones.includes(featureMilestone)) return false
      }

      return true
    })
  }, [data, query, missingRolloutIssue, activeFilter, today, dateRange, milestones])

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='features' isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Features' />

      <Page.FunctionBar>
        <Stack gap='sm' w='100%'>
          <FeatureFilters
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            dateRange={dateRange}
            setDateRange={setPersistedRange}
            milestones={milestones}
            setMilestones={setMilestones}
            missingRolloutIssue={missingRolloutIssue}
            setMissingRolloutIssue={setMissingRolloutIssue}
          />
          <TextInput
            autoFocus
            placeholder='Filter...'
            value={query}
            w='100%'
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </Stack>
      </Page.FunctionBar>
      <Page.Content className='overflow-y-auto'>
        <FeatureTable data={filtered} sorting={sorting} setSorting={setSorting} />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/feature/')({
  component: Features,
})
